import { boardInsertionIndex } from "./board-reorder.ts";
import { moveItem } from "./productivity.ts";

export interface BoardDragSnapshot {
  epicId?: string | null;
  id: string;
  position: number;
  tagOrder: string[];
  tags: Record<string, boolean>;
}

export interface BoardPlacement {
  epicId?: string | null;
  id: string;
  position: number;
  boardTag: string | null;
}

export interface BoardDrop {
  droppedId: string;
  nextColumn: string | null;
  placements: BoardPlacement[];
  original: BoardDragSnapshot[];
  destinationIndex: number;
}

function hiddenCardIds(column: HTMLElement): string[] {
  try {
    const ids: unknown = JSON.parse(column.dataset.boardHiddenIds || "[]");
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
  } catch { return []; }
}

export interface BoardDragActions {
  snapshots(visibleIds: Set<string>): BoardDragSnapshot[];
  drop(result: BoardDrop): void | Promise<void>;
  move(ids: string[], direction: -1 | 1, reveal?: { collectionId: string; column: string; index: number }): void | Promise<void>;
  hapticFeedback(): void;
  reducedMotion(): boolean;
}

export function bindBoardDragging(root: ParentNode, actions: BoardDragActions): void {
  let draggedId: string | null = null;
  let draggedCard: HTMLElement | null = null;
  let originContainer: HTMLElement | null = null;
  let originNextSibling: Element | null = null;
  let activeColumn: HTMLElement | null = null;
  let pendingPlacement: { column: HTMLElement; pointerY: number } | null = null;
  let placementFrame: number | null = null;
  let dropped = false;
  let original: BoardDragSnapshot[] = [];
  const movementAnimations = new WeakMap<HTMLElement, Animation>();

  const clearDragState = () => {
    if (placementFrame !== null) cancelAnimationFrame(placementFrame);
    placementFrame = null;
    pendingPlacement = null;
    activeColumn?.classList.remove("is-drag-over");
    root.querySelectorAll(".kanban-cards.is-board-drag-target").forEach(cards => cards.classList.remove("is-board-drag-target"));
    document.body.classList.remove("is-board-dragging");
  };
  const placeDraggedCard = (column: HTMLElement, pointerY: number) => {
    if (!draggedCard) return;
    const destination = column.querySelector<HTMLElement>(".kanban-cards");
    if (!destination) return;
    if (activeColumn !== column) {
      activeColumn?.classList.remove("is-drag-over");
      root.querySelectorAll(".kanban-cards.is-board-drag-target").forEach(cards => cards.classList.remove("is-board-drag-target"));
      activeColumn = column;
      activeColumn.classList.add("is-drag-over");
      destination.classList.add("is-board-drag-target");
    }
    const candidates = Array.from(destination.querySelectorAll<HTMLElement>(":scope > .kanban-card")).filter(card => card !== draggedCard);
    const insertionIndex = boardInsertionIndex(pointerY, candidates.map(card => {
      const bounds = card.getBoundingClientRect();
      return bounds.top + bounds.height / 2;
    }));
    const before = candidates[insertionIndex] || null;
    if (draggedCard.parentElement === destination && (before ? draggedCard.nextElementSibling === before : destination.lastElementChild === draggedCard)) return;
    const affectedContainers = new Set([draggedCard.parentElement, destination].filter((item): item is HTMLElement => item instanceof HTMLElement));
    const affectedCards = [...affectedContainers].flatMap(container => Array.from(container.querySelectorAll<HTMLElement>(":scope > .kanban-card")));
    const previousBounds = new Map(affectedCards.map(card => [card, card.getBoundingClientRect()]));
    destination.insertBefore(draggedCard, before);
    if (actions.reducedMotion()) return;
    affectedCards.forEach(card => {
      if (card === draggedCard || !card.isConnected) return;
      const previous = previousBounds.get(card);
      if (!previous) return;
      const current = card.getBoundingClientRect();
      const x = previous.left - current.left;
      const y = previous.top - current.top;
      if (Math.abs(x) < 1 && Math.abs(y) < 1) return;
      movementAnimations.get(card)?.cancel();
      movementAnimations.set(card, card.animate(
        [{ transform: `translate3d(${x}px,${y}px,0)` }, { transform: "translate3d(0,0,0)" }],
        { duration: 140, easing: "cubic-bezier(.2,.8,.2,1)" },
      ));
    });
  };
  const schedulePlacement = (column: HTMLElement, pointerY: number) => {
    pendingPlacement = { column, pointerY };
    if (placementFrame !== null) return;
    placementFrame = requestAnimationFrame(() => {
      placementFrame = null;
      const placement = pendingPlacement;
      pendingPlacement = null;
      if (placement) placeDraggedCard(placement.column, placement.pointerY);
    });
  };

  root.querySelectorAll<HTMLElement>(".kanban-card").forEach(card => {
    card.addEventListener("dragstart", event => {
      draggedId = card.dataset.todo || null;
      if (!draggedId) return;
      draggedCard = card;
      originContainer = card.parentElement;
      originNextSibling = card.nextElementSibling;
      dropped = false;
      const visibleIds = new Set([...Array.from(root.querySelectorAll<HTMLElement>(".kanban-card")).map(item => item.dataset.todo || ""), ...Array.from(root.querySelectorAll<HTMLElement>(".kanban-column:not(.is-collapsed)")).flatMap(hiddenCardIds)].filter(Boolean));
      original = actions.snapshots(visibleIds);
      card.classList.add("is-dragging", "is-board-insert-marker");
      document.body.classList.add("is-board-dragging");
      if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
      event.dataTransfer?.setData("text/plain", draggedId);
    });
    card.addEventListener("dragend", () => {
      if (!dropped && draggedCard && originContainer) originContainer.insertBefore(draggedCard, originNextSibling?.parentElement === originContainer ? originNextSibling : null);
      card.classList.remove("is-dragging", "is-board-insert-marker");
      clearDragState();
      draggedId = null;
      draggedCard = null;
      originContainer = null;
      originNextSibling = null;
      activeColumn = null;
    });
  });
  root.querySelectorAll<HTMLElement>(".kanban-column:not(.is-collapsed)").forEach(column => {
    column.addEventListener("dragover", event => {
      if (column.closest("[data-epic-archived=true]") && originContainer?.closest<HTMLElement>("[data-epic-id]")?.dataset.epicId !== column.dataset.epicId) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
      if (draggedCard) schedulePlacement(column, event.clientY);
    });
    column.addEventListener("dragleave", event => {
      if (column.contains(event.relatedTarget as Node | null) || activeColumn !== column) return;
      column.classList.remove("is-drag-over");
      column.querySelector(".kanban-cards")?.classList.remove("is-board-drag-target");
      activeColumn = null;
    });
    column.addEventListener("drop", event => {
      event.preventDefault();
      if (!draggedId || column.closest("[data-epic-archived=true]") && originContainer?.closest<HTMLElement>("[data-epic-id]")?.dataset.epicId !== column.dataset.epicId) return;
      dropped = true;
      if (placementFrame !== null) cancelAnimationFrame(placementFrame);
      placementFrame = null;
      pendingPlacement = null;
      placeDraggedCard(column, event.clientY);
      const result: BoardDrop = {
        droppedId: draggedId,
        nextColumn: column.dataset.fallback === "true" ? null : column.dataset.boardColumn || null,
        placements: Array.from(root.querySelectorAll<HTMLElement>(".kanban-column:not(.is-collapsed)")).flatMap(boardColumn => {
          const boardTag = boardColumn.dataset.fallback === "true" ? null : boardColumn.dataset.boardColumn || null;
          const visible = Array.from(boardColumn.querySelectorAll<HTMLElement>(".kanban-card")).map(card => card.dataset.todo || "").filter(Boolean);
          return [...visible, ...hiddenCardIds(boardColumn).filter(id => id !== draggedId)].map((id, position) => ({ id, position, boardTag, ...(boardColumn.hasAttribute("data-epic-id") ? { epicId: boardColumn.dataset.epicId || null } : {}) }));
        }),
        original: original.map(snapshot => ({ ...snapshot, tagOrder: [...snapshot.tagOrder], tags: { ...snapshot.tags } })),
        destinationIndex: Array.from(column.querySelectorAll<HTMLElement>(".kanban-card")).findIndex(card => card.dataset.todo === draggedId),
      };
      actions.hapticFeedback();
      void actions.drop(result);
    });
  });
  root.querySelectorAll<HTMLButtonElement>("[data-move-board-task]").forEach(button => button.addEventListener("click", () => {
    const card = button.closest<HTMLElement>(".kanban-card");
    const cards = card?.parentElement;
    if (!card || !cards) return;
    const rows = Array.from(cards.querySelectorAll<HTMLElement>(":scope > .kanban-card"));
    const direction: -1 | 1 = button.dataset.moveBoardTask === "up" ? -1 : 1;
    const column = card.closest<HTMLElement>(".kanban-column");
    const hiddenIds = column ? hiddenCardIds(column) : [];
    if (direction === 1 && rows.indexOf(card) === rows.length - 1 && hiddenIds.length && column) {
      const visibleIds = rows.map(item => item.dataset.todo || "").filter(Boolean);
      const movedId = visibleIds.pop()!;
      void actions.move([...visibleIds, hiddenIds[0], movedId, ...hiddenIds.slice(1)], direction, { collectionId: column.dataset.boardCollection || "", column: column.dataset.boardColumn || "", index: rows.length });
      return;
    }
    const reordered = moveItem(rows, rows.indexOf(card), direction);
    if (reordered.every((item, index) => item === rows[index])) return;
    void actions.move([...reordered.map(item => item.dataset.todo || "").filter(Boolean), ...hiddenIds], direction);
  }));
}
